import { useCallback, useEffect, useRef, useState } from "react";
import type {
  RemoteStageStream,
  Stage,
  StageParticipantInfo,
  StageStream,
} from "amazon-ivs-web-broadcast";
import { viewerToken } from "./api";
import {
  initialMemory,
  type InterruptionCause,
  type PlaybackMemory,
  type PlaybackState,
  readPlayback,
  type StageConnection,
} from "./playback";
import { followed, type Publishing } from "./publishers";

/**
 * Watching the show from the panel — the subscriber half of IVS real-time (`R-VID-9`, `R-HOST-19`).
 *
 * > *The host is shown what viewers are actually seeing, including the true delay, so they can
 * > pace their commentary against it.*
 *
 * **A clone of `apps/customer/src/room/useViewer.ts`**, for the reason set out at the top of
 * `playback.ts` beside it: sharing the two would mean lifting them into `packages/ui` and
 * editing the room to import them back, and the room is the one surface in this product that
 * must not break. The copy is the cheaper risk. Where the two differ, they differ deliberately
 * and each difference is marked below.
 *
 * ## Who this is for, and who it is not
 *
 * **Everybody on the panel except the person broadcasting.** A second host driving the running
 * order from a laptop, a moderator reading comments — they can see what the room sees and how far
 * behind it is. The presenter's own browser never mounts this: it already has a camera preview,
 * subscribing would cost a slot and some CPU, and a laptop that unmuted the stream in the same
 * physical room as the microphone would howl. `ShowPage` is what decides, and `Broadcast` tells
 * it whether this browser is the one publishing.
 *
 * ## Three differences from the room's copy
 *
 * - **It starts muted and stays muted until asked** (`mute`/`unmute` below), and the card says
 *   why. In the room, `R-VID-13` makes unmuting a primary control because the host's voice is the
 *   pitch; on a panel it is a monitor, and a second laptop in the same room is a feedback loop.
 * - **No `ended` state.** The card is only mounted while the show is live, so the fifth playback
 *   state has nowhere to come from — `R-END-*` is the room's concern.
 * - **The delay is always measured**, not hidden behind `?debug=1`. It is the whole point here:
 *   `R-VID-9` asks for the *true delay*, and a host pacing *going once* against it needs to see
 *   the number without knowing a query parameter exists.
 *
 * ## The SDK is imported lazily
 *
 * Less critical than in the room — the panel already carries `amazon-ivs-web-broadcast` for
 * publishing — but kept, because a panel open on the show list should not download a video SDK
 * and because it means this hook makes no difference to a page that never mounts it.
 *
 * ## A fresh token per join
 *
 * Every join, including every rejoin, asks the API for a new token. Nothing is cached and no
 * refresh timer exists, which is what makes recovery need no state that survived the failure.
 * The route is the **public** one a viewer uses — it authenticates optionally and works from this
 * hostname, exactly as the chat token does — so watching the show from the panel is watching the
 * show, with the same `SUBSCRIBE`-only capability and no new privilege anywhere.
 */

/** How long the sampler waits between readings. See `INTERRUPTED_AFTER_MS` for why it is short. */
const SAMPLE_MS = 400;

/**
 * How long our own connection has to be down before we tear it up and rejoin.
 *
 * Only for an interruption that is ours or unattributable. When the *publisher* is the one who
 * stopped (`cause: "host"`), rejoining achieves nothing: our session is healthy, there is simply
 * nobody publishing into it, and the stream reappears on its own when they come back — which is
 * `R-VID-16`'s *recovery is automatic* without a reconnection at all.
 */
const REJOIN_AFTER_MS = 2_000;

/** Backoff for repeated failures, in milliseconds, then held at the last value. */
const REJOIN_BACKOFF_MS = [0, 1_000, 2_000, 5_000, 10_000];

/**
 * How many times a **join** may fail before the room stops trying and says so.
 *
 * Only joins. An interruption caused by the host is not counted — there is nothing to retry there,
 * the stream reappears on its own — and a viewer whose own network dropped keeps reconnecting for
 * as long as they are in the room.
 *
 * A cap exists because the alternative, for a viewer on an unsupported browser, is a spinner for
 * ever, a console filling with `ICE failed`, and no statement anywhere that this was never going
 * to work. Five attempts is about twenty seconds with the backoff above — long enough
 * to ride out a bad moment, short enough that a dead end is named while somebody is still looking.
 */
const MAX_JOIN_ATTEMPTS = 5;

export interface MonitorPlayback {
  state: PlaybackState;
  cause: InterruptionCause | undefined;
  /** Attach to a `<video muted autoPlay playsInline>`. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /**
   * Muted, and it stays that way until somebody presses the control.
   *
   * Browsers insist on it for autoplay, and here it is also the right default on its own terms:
   * a panel open on a laptop in the same room as the presenter's microphone is a feedback loop
   * waiting for a click, so the card says so beside the button.
   */
  muted: boolean;
  unmute: () => void;
  mute: () => void;
  /**
   * The playout delay this viewer is actually experiencing, in milliseconds, or `undefined` before
   * it can be measured.
   *
   * **Not glass-to-glass** (`R-VID-3`), and the difference matters enough to name: it is the jitter
   * buffer plus the network round trip as WebRTC reports them, which excludes the host's capture
   * and encode at one end and this browser's display pipeline at the other. It is the largest and
   * most variable part of the total, and it is the part that regresses — so it is the right thing
   * to watch (`R-VID-5`) and the wrong thing to quote as the figure in the specification. The
   * stopwatch procedure in `docs/runbooks/video.md` §4.4 is how the real number is taken.
   */
  latencyMs: number | undefined;
  /** A viewer-visible failure that is not about the stream: no token, or the show is not live. */
  failure: string | undefined;
  /**
   * True once joining has failed as often as it is going to be tried (`MAX_JOIN_ATTEMPTS`).
   *
   * The room shows a designed state and a way to try again rather than a spinner that never
   * resolves — `R-VID-10a`'s reasoning, and the condition it most often means in practice is a
   * browser the provider does not support.
   */
  givenUp: boolean;
  /** Starts again from the beginning, for the button on that state. */
  retry: () => void;
}

export function useMonitor(publicId: string, live: boolean): MonitorPlayback {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<PlaybackState>("connecting");
  const [cause, setCause] = useState<InterruptionCause | undefined>(undefined);
  const [muted, setMuted] = useState(true);
  const [latencyMs, setLatencyMs] = useState<number | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const [givenUp, setGivenUp] = useState(false);
  /** Bumped by `retry`, which re-runs the effect below from scratch. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Nothing to subscribe to unless the show is live. `ShowPage` also only mounts the card
    // then, so this is the second of two guards rather than the only one — and it is the one
    // that matters when a show ends while somebody is watching it.
    if (!live) return;

    /**
     * Everything below closes over this. It is a plain object rather than refs because the whole
     * lifetime is one effect: a `publicId` that changes, or a show that ends, tears the connection
     * down and builds a new one, which is the only correct response to either.
     */
    const session = {
      stopped: false,
      stage: undefined as Stage | undefined,
      connection: "connecting" as StageConnection,
      /**
       * Every remote publisher's tracks, by participant id, then by stream id. Audio and video
       * arrive as separate streams. Only the followed participant's are ever attached (`R-OBS-6`).
       */
      publishers: new Map<
        string,
        Publishing & { streams: Map<string, RemoteStageStream> }
      >(),
      /** Advanced for each participant that starts publishing, for `Publishing.since`. */
      published: 0,
      /** The participant whose tracks are attached — see `publishers.ts`. */
      following: undefined as string | undefined,
      videoStopped: false,
      /**
       * Participants whose subscription IVS reported as errored — a media path this browser could
       * not build. Cleared by a subscription that then succeeds, so a failure that recovers on a
       * retry stops being reported.
       */
      subscribeFailed: new Set<string>(),
      memory: initialMemory as PlaybackMemory,
      /** The last state logged, so the development trace prints transitions rather than samples. */
      reported: undefined as PlaybackState | undefined,
      interruptedSince: undefined as number | undefined,
      attempts: 0,
      timer: undefined as ReturnType<typeof setInterval> | undefined,
      rejoin: undefined as ReturnType<typeof setTimeout> | undefined,
      /**
       * Which attempt is the current one.
       *
       * Two problems, one counter. A rejoin takes a token round trip and a dynamic import, during
       * which the sampler keeps running and would happily start a second — so an attempt claims
       * the next number and abandons itself if somebody else has claimed one since. And the
       * abandoned attempt's `Stage` may still emit: its handlers hold this closure, so a late
       * *streams removed* from a connection we walked away from would otherwise clear the tracks
       * of the one that replaced it, and the viewer would be told the host had gone.
       */
      generation: 0,
    };

    /** The followed publisher's streams — the only ones the element ever plays. */
    function held(): RemoteStageStream[] {
      const one = session.following && session.publishers.get(session.following);
      return one ? [...one.streams.values()] : [];
    }

    /**
     * Re-decides who is followed after a publisher's streams changed, and re-attaches when that
     * is what changed (`R-OBS-6`).
     *
     * A publisher who is not followed coming or going leaves the element alone — rebuilding its
     * source is a visible blip, and nothing the monitor is showing has changed.
     */
    function refollow(changed: string) {
      const next = followed(session.publishers);
      if (next !== session.following) {
        session.following = next;
        // A different picture, with its own mute state. The playback counter needs nothing —
        // see `publishers.ts`.
        const video = held().find((one) => one.mediaStreamTrack.kind === "video");
        session.videoStopped = video?.isMuted ?? false;
        attach();
        return;
      }
      if (changed === session.following) attach();
    }

    /** Rebuilds the element's source from the followed publisher's tracks. */
    function attach() {
      const element = videoRef.current;
      if (!element) return;
      const tracks = held().map((one) => one.mediaStreamTrack);
      if (tracks.length === 0) {
        element.srcObject = null;
        return;
      }
      element.srcObject = new MediaStream(tracks);
      // Muted autoplay is assumed, so the element plays silently until somebody asks otherwise.
      // A play() that is rejected is not an error worth surfacing: the element is muted, so
      // browsers allow it, and the one case that fails is a tab that is not visible.
      void element.play().catch(() => undefined);
    }

    async function sample() {
      const streams = held();
      const video = streams.find((one) => one.mediaStreamTrack.kind === "video");
      const audio = streams.find((one) => one.mediaStreamTrack.kind === "audio");

      let progress: number | undefined;
      let jitterMs: number | undefined;
      let rttMs: number | undefined;

      /**
       * Video where there is video, audio where there is not.
       *
       * `framesDecoded` is the signal `R-VID-15` is measured against, and it is the property name
       * WebRTC uses — not ours. On an **audio-only** broadcast there is no such counter and no
       * picture to count: a host whose camera would not open publishes a voice, which `R-VID-7`
       * prefers to nothing, and a viewer of that must not be told the stream has been interrupted
       * because no frames are arriving. `packetsReceived` on the audio track is the same kind of
       * fact — a number that climbs while media flows and stops when it does not.
       */
      const source = video ?? audio;
      const kind = video ? "video" : "audio";

      if (source) {
        try {
          const report = await source.requestRTCStats();
          report?.forEach((entry: Record<string, unknown>) => {
            if (entry.type === "inbound-rtp" && entry.kind === kind) {
              const counter =
                kind === "video" ? entry.framesDecoded : entry.packetsReceived;
              if (typeof counter === "number") progress = counter;
              const delay = entry.jitterBufferDelay;
              const emitted = entry.jitterBufferEmittedCount;
              if (
                typeof delay === "number" &&
                typeof emitted === "number" &&
                emitted > 0
              ) {
                jitterMs = (delay / emitted) * 1000;
              }
            }
            if (
              entry.type === "candidate-pair" &&
              entry.nominated === true &&
              typeof entry.currentRoundTripTime === "number"
            ) {
              rttMs = entry.currentRoundTripTime * 1000;
            }
          });
        } catch {
          // Statistics are best-effort; the fallback below is what keeps detection working.
        }
      }

      /**
       * The fallback, and the reason there is one: the statistics above are the right signal and
       * they are the SDK's to give. If a browser or a future SDK version stops answering, silence
       * would read as a stopped stream and every viewer would be told the show had been
       * interrupted while they were watching it. `getVideoPlaybackQuality` is a DOM API on the
       * element itself and counts the same thing from the other end.
       */
      if (progress === undefined && video) {
        const element = videoRef.current;
        progress = element?.getVideoPlaybackQuality?.().totalVideoFrames;
      }

      const reading = readPlayback(session.memory, {
        at: performance.now(),
        connection: session.connection,
        online: navigator.onLine,
        // Any remote stream, not only a video one. An audio-only broadcast is a degraded show and
        // not an absent one.
        streamPresent: source !== undefined,
        streamStopped: session.videoStopped,
        // The followed publisher's subscription, or — before anybody's streams have arrived — any
        // at all: a subscription that errors never delivers a stream to follow.
        subscribeFailed: session.following
          ? session.subscribeFailed.has(session.following)
          : session.subscribeFailed.size > 0,
        progress,
        showEnded: false,
      });

      if (session.stopped) return;

      if (import.meta.env.DEV && reading.state !== session.reported) {
        /**
         * **Every playback transition, with how long the stream had been silent when it was made.**
         *
         * `R-VID-15` gives detection two seconds, and *silentFor* is the number that requirement is
         * about — the gap between the last frame and the verdict. Eyeballing a screen cannot tell
         * 1.6 seconds from 2.4, and this is what turns the done-condition's *a deliberate network
         * drop is detected inside two seconds* into a reading somebody can write down. Dev only.
         */
        const silence =
          reading.memory.lastProgressAt === undefined
            ? undefined
            : Math.round(performance.now() - reading.memory.lastProgressAt);
        console.debug("[panel] monitor", {
          from: session.reported ?? "—",
          to: reading.state,
          cause: reading.cause ?? "—",
          silentForMs: silence,
          connection: session.connection,
          online: navigator.onLine,
        });
        session.reported = reading.state;
      }

      session.memory = reading.memory;
      setState(reading.state);
      setCause(reading.cause);
      setLatencyMs(
        jitterMs === undefined ? undefined : Math.round(jitterMs + (rttMs ?? 0) / 2),
      );

      // Recovery (`R-VID-16`), and only for the interruptions rejoining can fix.
      const ours =
        reading.state === "interrupted" &&
        (reading.cause === "viewer" || reading.cause === "unknown");
      if (!ours) {
        session.interruptedSince = undefined;
        return;
      }
      session.interruptedSince ??= performance.now();
      if (performance.now() - session.interruptedSince >= REJOIN_AFTER_MS) {
        session.interruptedSince = undefined;
        void connect();
      }
    }

    /** Joins the stage, from scratch, discarding whatever was there before. */
    async function connect(): Promise<void> {
      if (session.stopped) return;
      const mine = ++session.generation;
      /** True while this attempt is still the current one. */
      const current = () => !session.stopped && session.generation === mine;

      if (session.stage) {
        try {
          session.stage.leave();
        } catch {
          // Leaving a connection that is already gone is not a failure.
        }
        session.stage = undefined;
      }
      session.publishers.clear();
      session.following = undefined;
      session.videoStopped = false;
      session.subscribeFailed.clear();
      session.connection = "connecting";
      attach();

      if (session.attempts >= MAX_JOIN_ATTEMPTS) {
        // Said once, rather than tried for ever. `givenUp` is what the room renders a state from.
        console.error(
          `Gave up joining the show's stage after ${session.attempts} attempts`,
        );
        setGivenUp(true);
        return;
      }

      const wait =
        REJOIN_BACKOFF_MS[Math.min(session.attempts, REJOIN_BACKOFF_MS.length - 1)]!;
      session.attempts += 1;
      if (wait > 0) {
        await new Promise<void>((resolve) => {
          session.rejoin = setTimeout(resolve, wait);
        });
        if (!current()) return;
      }

      let token: string;
      try {
        token = (await viewerToken(publicId)).token;
      } catch (error) {
        if (!current()) return;
        // A show that is live and has no stream yet is the one failure worth naming to a viewer,
        // and it is temporary by construction — see `viewer-token.ts`. Everything else is reported
        // as a stream problem, because from a customer's chair it is one.
        setFailure(error instanceof Error ? error.message : undefined);
        session.connection = "errored";
        void connect();
        return;
      }

      setFailure(undefined);

      const sdk = await import("amazon-ivs-web-broadcast");
      if (!current()) return;

      const stage = new sdk.Stage(token, {
        // A viewer never publishes. The token says so too, and IVS enforces that end of it —
        // spec §12.9 is a property of the capability, not of this strategy (`viewer-token.ts`).
        stageStreamsToPublish: () => [],
        shouldPublishParticipant: () => false,
        shouldSubscribeToParticipant: () => sdk.SubscribeType.AUDIO_VIDEO,
        /**
         * The jitter buffer is left at IVS's default deliberately. A lower setting buys tens of
         * milliseconds off a budget that already has room — spec §12.6 delivers under 300ms
         * against `R-VID-3`'s 500 — and pays for them in stalls, which `R-VID-11` is explicit
         * about preferring not to have: *a stalled stream during an auction is worse than a soft
         * one.* Tuning this is a decision to revisit with a real audience and real numbers, not
         * one to make from a specification.
         */
      });

      stage.on(sdk.StageEvents.STAGE_CONNECTION_STATE_CHANGED, (next) => {
        if (!current()) return;
        session.connection = next as StageConnection;
        if (next === sdk.StageConnectionState.CONNECTED) session.attempts = 0;
      });

      stage.on(
        sdk.StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED,
        (participant: StageParticipantInfo, streams: StageStream[]) => {
          if (participant.isLocal || !current()) return;
          let one = session.publishers.get(participant.id);
          if (!one) {
            // Somebody new to publish, and so — by `R-OBS-6` — the one to follow.
            one = { since: ++session.published, streams: new Map() };
            session.publishers.set(participant.id, one);
          }
          for (const stream of streams) {
            one.streams.set(stream.id, stream as RemoteStageStream);
            if (
              stream.mediaStreamTrack.kind === "video" &&
              participant.id === session.following
            ) {
              session.videoStopped = false;
            }
          }
          refollow(participant.id);
        },
      );

      stage.on(
        sdk.StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED,
        (participant: StageParticipantInfo, streams: StageStream[]) => {
          if (participant.isLocal || !current()) return;
          const one = session.publishers.get(participant.id);
          if (!one) return;
          for (const stream of streams) one.streams.delete(stream.id);
          // A participant with nothing left is not publishing, and must not be followed.
          if (one.streams.size === 0) session.publishers.delete(participant.id);
          refollow(participant.id);
        },
      );

      /**
       * A participant gone altogether. Their streams' removal normally says so first; this is
       * the belt to that, so a publisher who left can never stay the one being followed.
       */
      stage.on(
        sdk.StageEvents.STAGE_PARTICIPANT_LEFT,
        (participant: StageParticipantInfo) => {
          if (participant.isLocal || !current()) return;
          session.subscribeFailed.delete(participant.id);
          if (!session.publishers.delete(participant.id)) return;
          refollow(participant.id);
        },
      );

      /**
       * The publisher turning their camera off, or IVS noticing that their video stopped. The
       * fastest of the three signals this hook has, and the only one that is unambiguous about
       * whose end failed — which is why `readPlayback` trusts it immediately rather than waiting
       * out a timer (`R-VID-17`).
       */
      stage.on(
        sdk.StageEvents.STAGE_STREAM_MUTE_CHANGED,
        (participant: StageParticipantInfo, stream: StageStream) => {
          // Only the picture being shown. Another publisher's camera going off is not ours.
          if (participant.isLocal || !current() || participant.id !== session.following)
            return;
          if (stream.mediaStreamTrack.kind === "video")
            session.videoStopped = stream.isMuted;
        },
      );

      /**
       * **Whether this browser could build a media path to the publisher at all.**
       *
       * The one signal that says *the failure is ours and the show is fine*. IVS reports a
       * subscription as `ERRORED` when it cannot be established, which is what a failed ICE
       * negotiation looks like from up here — Firefox logs `ICE failed, add a STUN server` and
       * Chromium logs almost nothing, so the console is not a reliable place to learn it.
       *
       * Without this the case is indistinguishable from a stream that has stopped: the stage
       * session is *connected*, the publisher's streams are attached, and no frame ever decodes —
       * so `readPlayback` waits out the grace period and reports `unknown`, and the card says the
       * stream has stopped and will come back on its own. Both false. See `blocked` in
       * `playback.ts`.
       *
       * Listened to rather than inferred, and it is **additive**: if the SDK never emits it, the
       * hook falls back to the frame counter alone.
       */
      stage.on(
        sdk.StageEvents.STAGE_PARTICIPANT_SUBSCRIBE_STATE_CHANGED,
        (participant: StageParticipantInfo, state: string) => {
          if (participant.isLocal || !current()) return;
          if (state === sdk.StageParticipantSubscribeState.ERRORED) {
            session.subscribeFailed.add(participant.id);
            return;
          }
          // Any other state means it is being attempted or has succeeded, so a previous failure
          // is no longer the truth about this participant.
          if (state === sdk.StageParticipantSubscribeState.SUBSCRIBED) {
            session.subscribeFailed.delete(participant.id);
          }
        },
      );

      session.stage = stage;

      try {
        await stage.join();
        // A join that lands after this attempt was superseded is a connection nobody is tracking,
        // holding a subscriber slot for as long as the page is open. Give it back.
        if (!current()) stage.leave();
      } catch (error) {
        if (!current()) return;
        console.error("Could not join the show's stage", error);
        session.connection = "errored";
        void connect();
      }
    }

    void connect();
    session.timer = setInterval(() => void sample(), SAMPLE_MS);

    // Captured now rather than read in the cleanup: by the time cleanup runs the ref may already
    // point at a different element, or at none, and what needs detaching is the one this session
    // attached its tracks to.
    const attached = videoRef.current;

    return () => {
      session.stopped = true;
      if (session.timer) clearInterval(session.timer);
      if (session.rejoin) clearTimeout(session.rejoin);
      try {
        session.stage?.leave();
      } catch {
        // Nothing to do about a failed leave on the way out.
      }
      if (attached) attached.srcObject = null;
    };
  }, [publicId, live, attempt]);

  const retry = useCallback(() => {
    setGivenUp(false);
    // A new effect run, which builds a fresh session with its attempt count back at zero.
    setAttempt((previous) => previous + 1);
  }, []);

  const unmute = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;
    element.muted = false;
    // The unmute is the user gesture, so this `play()` is the one that is allowed to produce
    // sound. Without it a paused element stays silent and the control appears not to work.
    void element.play().catch(() => undefined);
    setMuted(false);
  }, []);

  const mute = useCallback(() => {
    const element = videoRef.current;
    if (element) element.muted = true;
    setMuted(true);
  }, []);

  return {
    state,
    cause,
    videoRef,
    muted,
    unmute,
    mute,
    latencyMs,
    failure,
    // Derived rather than cleared on the way in: having given up means nothing about a show that
    // is not live, and a state written from inside the effect would be a second source of truth
    // for something the show already decides.
    givenUp: givenUp && live,
    retry,
  };
}
